import {
  bootstrapApplication,
  createApplication,
} from "@angular/platform-browser";
import { createCustomElement } from "@angular/elements";
import { appConfig } from "./app/app.config";
import { AppComponent } from "./app/app.component";
import { CustomerPortalSettingsComponent } from "./app/features/settings/components/customer-portal-settings/customer-portal-settings.component";
import { ScheduledOrdersListComponent } from "./app/features/scheduled-orders/pages/scheduled-orders-list/scheduled-orders-list.component";
import { CreateScheduledOrderComponent } from "./app/features/scheduled-orders/pages/create/create-scheduled-order.component";
import { EditScheduledOrderComponent } from "./app/features/scheduled-orders/pages/edit/edit-scheduled-order.component";
import { DashboardProcessingCycleViewComponent } from "./app/features/scheduled-orders/components/dashboard-processing-cycle-view/dashboard-processing-cycle-view.component";
import { CustomersListComponent } from "./app/features/customers/pages/customers-list/customers-list.component";
import { CustomerEditComponent } from "./app/features/customers/pages/customer-edit/customer-edit.component";
import { SiteDunningSettingsComponent } from "./app/features/settings/components/site-dunning-settings/site-dunning-settings.component";
import { RetentionWorkflowsListComponent } from "./app/features/retain-and-grow/pages/retention-workflows-list/retention-workflows-list.component";
import { RetentionWorkflowCreateEditComponent } from "./app/features/retain-and-grow/pages/retention-workflow-create-edit/retention-workflow-create-edit.component";
import { CouponsListComponent } from "./app/features/coupons/pages/coupons-list/coupons-list.component";
import { CouponEditComponent } from "./app/features/coupons/pages/coupon-edit/coupon-edit.component";
import { DashboardContainerComponent } from "./app/features/dashboard/components/dashboard-container/dashboard-container.component";
import { ProductsListComponent } from "./app/features/products/pages/products-list/products-list.component";
import { ProductDetailComponent } from "./app/features/products/pages/product-detail/product-detail.component";
import { ProductGroupsListComponent } from "./app/features/product-groups/pages/product-groups-list/product-groups-list.component";
import { ProductGroupEditComponent } from "./app/features/product-groups/pages/product-group-edit/product-group-edit.component";
import { QuickLinksWrapperComponent } from "./app/features/quick-links/pages/quick-links-wrapper/quick-links-wrapper.component";
import { QuickLinkFormPageComponent } from "./app/features/quick-links/pages/quick-link-form-page/quick-link-form-page.component";
import { PaymentIntegrationsListComponent } from "./app/features/payment-integrations/pages/payment-integrations-list/payment-integrations-list.component";
import { PaymentIntegrationUpsertComponent } from "./app/features/payment-integrations/pages/payment-integration-upsert/payment-integration-upsert.component";

// Check if we're in embedded context (WordPress/external)
// Also check if app-root element exists - if not, assume embedded mode
const isEmbedded =
  window.parent !== window ||
  window.location !== window.parent.location ||
  !document.querySelector("app-root");

// If not embedded, bootstrap the main app first, then create custom elements
// If embedded, only create custom elements
if (!isEmbedded) {
  // Main app mode
  bootstrapApplication(AppComponent, appConfig)
    .then(() => {
      // Also create custom elements for potential use
      return createCustomElements();
    })
    .catch((err) => console.error("QMC initialization error:", err));
} else {
  // Embedded mode - only create custom elements
  createCustomElements().catch((err) =>
    console.error("QMC initialization error:", err),
  );
}

// Create custom elements for WordPress integration
async function createCustomElements() {
  try {
    const app = await createApplication(appConfig);

    // Customer Portal Settings
    const customerPortalSettings = createCustomElement(
      CustomerPortalSettingsComponent,
      {
        injector: app.injector,
      },
    );
    customElements.define(
      "qmc-customer-portal-settings",
      customerPortalSettings,
    );

    // Scheduled Orders List
    console.log("Creating scheduled-orders-list element...");
    const scheduledOrdersList = createCustomElement(
      ScheduledOrdersListComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Scheduled-orders-list element created");
    customElements.define("qmc-scheduled-orders-list", scheduledOrdersList);

    // Create Scheduled Order
    console.log("Creating create-scheduled-order element...");
    const createScheduledOrder = createCustomElement(
      CreateScheduledOrderComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Create-scheduled-order element created");
    customElements.define("qmc-create-scheduled-order", createScheduledOrder);

    // Edit Scheduled Order
    console.log("Creating edit-scheduled-order element...");
    const editScheduledOrder = createCustomElement(
      EditScheduledOrderComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Edit-scheduled-order element created");
    customElements.define("qmc-edit-scheduled-order", editScheduledOrder);

    // Dashboard Processing Cycle View
    console.log("Creating processing-cycle-view element...");
    const processingCycleView = createCustomElement(
      DashboardProcessingCycleViewComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Processing-cycle-view element created");
    customElements.define("qmc-processing-cycle-view", processingCycleView);

    // Customers List
    console.log("Creating customers-list element...");
    const customersList = createCustomElement(CustomersListComponent, {
      injector: app.injector,
    });
    console.log("Customers-list element created");
    customElements.define("qmc-customers-list", customersList);

    // Customer Edit
    console.log("Creating customer-edit element...");
    const customerEdit = createCustomElement(CustomerEditComponent, {
      injector: app.injector,
    });
    console.log("Customer-edit element created");
    customElements.define("qmc-customer-edit", customerEdit);

    // Dunning Settings
    console.log("Creating dunning-settings element...");
    const dunningSettings = createCustomElement(SiteDunningSettingsComponent, {
      injector: app.injector,
    });
    console.log("Dunning-settings element created");
    customElements.define("qmc-dunning-settings", dunningSettings);

    // Retention Workflows List
    console.log("Creating retention-workflows-list element...");
    const retentionWorkflowsList = createCustomElement(
      RetentionWorkflowsListComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Retention-workflows-list element created");
    customElements.define(
      "qmc-retention-workflows-list",
      retentionWorkflowsList,
    );

    // Retention Workflow Create/Edit
    console.log("Creating retention-workflow-create-edit element...");
    const retentionWorkflowCreateEdit = createCustomElement(
      RetentionWorkflowCreateEditComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Retention-workflow-create-edit element created");
    customElements.define(
      "qmc-retention-workflow-create-edit",
      retentionWorkflowCreateEdit,
    );

    // Coupons List
    console.log("Creating coupons-list element...");
    const couponsList = createCustomElement(CouponsListComponent, {
      injector: app.injector,
    });
    console.log("Coupons-list element created");
    customElements.define("qmc-coupons-list", couponsList);

    // Coupon Edit (Create/Edit)
    console.log("Creating coupon-edit element...");
    const couponEdit = createCustomElement(CouponEditComponent, {
      injector: app.injector,
    });
    console.log("Coupon-edit element created");
    customElements.define("qmc-coupon-edit", couponEdit);

    // Dashboard Container
    console.log("Creating dashboard-container element...");
    const dashboardContainer = createCustomElement(
      DashboardContainerComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Dashboard-container element created");
    customElements.define("qmc-dashboard-container", dashboardContainer);

    // Products List
    console.log("Creating products-list element...");
    const productsList = createCustomElement(ProductsListComponent, {
      injector: app.injector,
    });
    console.log("Products-list element created");
    customElements.define("qmc-products-list", productsList);

    // Product Detail
    console.log("Creating product-detail element...");
    const productDetail = createCustomElement(ProductDetailComponent, {
      injector: app.injector,
    });
    console.log("Product-detail element created");
    customElements.define("qmc-product-detail", productDetail);

    // Product Groups List
    console.log("Creating product-groups-list element...");
    const productGroupsList = createCustomElement(ProductGroupsListComponent, {
      injector: app.injector,
    });
    console.log("Product-groups-list element created");
    customElements.define("qmc-product-groups-list", productGroupsList);

    // Product Group Edit (Create/Edit)
    console.log("Creating product-group-edit element...");
    const productGroupEdit = createCustomElement(ProductGroupEditComponent, {
      injector: app.injector,
    });
    console.log("Product-group-edit element created");
    customElements.define("qmc-product-group-edit", productGroupEdit);

    // Quick Links List
    console.log("Creating quick-links-list element...");
    const quickLinksList = createCustomElement(QuickLinksWrapperComponent, {
      injector: app.injector,
    });
    console.log("Quick-links-list element created");
    customElements.define("qmc-quick-links-list", quickLinksList);

    // Quick Link Form (Create/Edit)
    console.log("Creating quick-link-form element...");
    const quickLinkForm = createCustomElement(QuickLinkFormPageComponent, {
      injector: app.injector,
    });
    console.log("Quick-link-form element created");
    customElements.define("qmc-quick-link-form", quickLinkForm);

    // Payment Integrations List
    console.log("Creating payment-integrations-list element...");
    const paymentIntegrationsList = createCustomElement(
      PaymentIntegrationsListComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Payment-integrations-list element created");
    customElements.define(
      "qmc-payment-integrations-list",
      paymentIntegrationsList,
    );

    // Payment Integration Upsert (Create/Edit)
    console.log("Creating payment-integration-upsert element...");
    const paymentIntegrationUpsert = createCustomElement(
      PaymentIntegrationUpsertComponent,
      {
        injector: app.injector,
      },
    );
    console.log("Payment-integration-upsert element created");
    customElements.define(
      "qmc-payment-integration-upsert",
      paymentIntegrationUpsert,
    );

    // List of registered elements
    const elements = [
      "qmc-customer-portal-settings",
      "qmc-scheduled-orders-list",
      "qmc-create-scheduled-order",
      "qmc-edit-scheduled-order",
      "qmc-processing-cycle-view",
      "qmc-customers-list",
      "qmc-customer-edit",
      "qmc-dunning-settings",
      "qmc-retention-workflows-list",
      "qmc-retention-workflow-create-edit",
      "qmc-coupons-list",
      "qmc-coupon-edit",
      "qmc-dashboard-container",
      "qmc-products-list",
      "qmc-product-detail",
      "qmc-product-groups-list",
      "qmc-product-group-edit",
      "qmc-quick-links-list",
      "qmc-quick-link-form",
      "qmc-payment-integrations-list",
      "qmc-payment-integration-upsert",
    ];

    // Notify parent window that elements are ready (for WordPress integration)
    if (isEmbedded && window.parent) {
      window.parent.postMessage(
        {
          type: "qmc-ready",
          elements: elements,
        },
        "*",
      );
    }

    // Also dispatch a custom event for local integration
    window.dispatchEvent(
      new CustomEvent("qmc-elements-ready", {
        detail: {
          elements: elements,
        },
      }),
    );
  } catch (err) {
    console.error("Failed to create custom elements:", err);

    // Notify parent window if embedded
    if (isEmbedded && window.parent) {
      window.parent.postMessage(
        {
          type: "qmc-error",
          error: err instanceof Error ? err.message : String(err),
        },
        "*",
      );
    }
  }
}
